'use client'

import { createContext, FC, ReactNode, useContext, useRef } from 'react'
import { customFetch, Params } from './custom.fetch.func'

let isRefreshing = false

const FetchProvider = createContext(
  customFetch({
    baseUrl: '',
    beforeRequest: async (url: string, options: RequestInit): Promise<RequestInit> => {
      if (typeof window === 'undefined') {
        const { cookies } = await import('next/headers')
        const c = await cookies()

        return {
          ...options,
          headers: {
            Cookie: c.toString(),
          },
        }
      } else {
        return options
      }
    },
    afterRequest: async (url: string, options: RequestInit, response: Response): Promise<Response> => {
      if (response.status === 401 && typeof window !== 'undefined' && !url.includes('auth/refresh')) {
        if (!isRefreshing) {
          isRefreshing = true
          try {
            // Attempt to refresh tokens against backend directly (cookies included)
            const refreshTokensResponse = await customFetch({
              baseUrl: process.env.NEXT_PUBLIC_API_URL!,
            })('auth/refresh', {
              method: 'POST',
              cache: 'no-cache',
            })

            // On success, retry original request
            if (refreshTokensResponse.ok) {
              const retryResponse = await customFetch({
                baseUrl: process.env.NEXT_PUBLIC_API_URL!,
              })(url, {
                ...options,
              })
              return retryResponse
            }
          } catch (_) {
            // swallow and fall through
          } finally {
            isRefreshing = false
          }
        }
      }

      return response
    },
  } as Params)
)

export const FetchWrapperComponent: FC<Params & { children: ReactNode }> = (props) => {
  const { children, ...params } = props
  const fetchData = useRef(customFetch(params))
  return <FetchProvider.Provider value={fetchData.current}>{children}</FetchProvider.Provider>
}

export const useFetch = () => {
  return useContext(FetchProvider)
}
